import React, { useRef } from 'react'
import {
  UserFeedback,
  Mtds,
  useAuth,
  ChatCenteredIcon,
  ListBulletsIcon,
  ChartPieSliceIcon,
  GearIcon,
  HandshakeIcon,
  CaretRightIcon,
  ListChecksIcon,
  TestTubeIcon,
  useBetaFeature,
  useGetWorkspaceGroupsQuery,
  useMtdsAppSticky
} from 'prove-common'
import { Link, NavLink, useLocation, useSearchParams } from 'react-router'
import { TASKS_BETA_FEATURE_KEY } from '../../betaFeatures'
import {
  isWorkspaceId,
  parseWorkspaceId,
  useIsWorkspaceFeatureEnabled,
  WORKSPACE_QUERY_PARAM,
  toWorkspaceViewModels
} from '../../workspaces/workspaces'

export const SideBarLayout = () => {
  const stickyRef = useRef<HTMLElement>(null)
  const { isAdmin } = useAuth()
  const isWorkspaceFeatureEnabled = useIsWorkspaceFeatureEnabled().enabled
  const isTasksFeatureEnabled = useBetaFeature(TASKS_BETA_FEATURE_KEY).enabled
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { data: groups = [] } = useGetWorkspaceGroupsQuery(
    isWorkspaceFeatureEnabled
  )
  const workspaces = toWorkspaceViewModels(
    groups.flatMap((group) => group.workspaces)
  )
  const workspaceId = parseWorkspaceId(searchParams.get(WORKSPACE_QUERY_PARAM))
  const isWorkspaceScopedPage =
    location.pathname === '/plans' || location.pathname === '/tasks'
  const selectedWorkspaceId =
    isWorkspaceFeatureEnabled &&
    isWorkspaceScopedPage &&
    isWorkspaceId(workspaceId, workspaces)
      ? workspaceId
      : undefined
  const tasksTo =
    selectedWorkspaceId == null
      ? '/tasks'
      : `/tasks?${WORKSPACE_QUERY_PARAM}=${selectedWorkspaceId}`
  useMtdsAppSticky(stickyRef)

  return (
    <>
      <Mtds.App.Toggle id={'mtds-app-toggle'} />
      <Mtds.App.Sidebar>
        <Mtds.App.Sticky as={Mtds.Prose} ref={stickyRef}>
          <menu>
            <li>
              <Mtds.Button
                as={NavLink}
                data-tooltip='Statistikk'
                to='/statistics'
              >
                <ChartPieSliceIcon />
              </Mtds.Button>
            </li>
            <li>
              <Mtds.Button as={NavLink} data-tooltip='Prøver' to='/samples'>
                <TestTubeIcon />
              </Mtds.Button>
            </li>
            {isTasksFeatureEnabled && (
              <li>
                <Mtds.Button as={NavLink} data-tooltip='Oppgaver' to={tasksTo}>
                  <ListChecksIcon />
                </Mtds.Button>
              </li>
            )}
            {!isWorkspaceFeatureEnabled && (
              <>
                <li>
                  <Mtds.Button as={NavLink} data-tooltip='Planer' to='/plans'>
                    <ListBulletsIcon />
                  </Mtds.Button>
                </li>
              </>
            )}
          </menu>

          {isWorkspaceFeatureEnabled &&
            groups.map((group) => (
              <React.Fragment key={group.kind.code}>
                <Mtds.Divider />
                <Mtds.Heading as='h2' data-size='2xs' data-app-expanded='true'>
                  {group.kind.title}
                </Mtds.Heading>
                <menu>
                  {group.workspaces.map((workspace) => (
                    <li key={workspace.id}>
                      <Mtds.Button
                        as={Link}
                        aria-current={
                          workspace.id === selectedWorkspaceId
                            ? 'page'
                            : undefined
                        }
                        data-tooltip={workspace.name}
                        to={`/plans?${WORKSPACE_QUERY_PARAM}=${workspace.id}`}
                      >
                        <span aria-hidden='true' data-app-expanded='false'>
                          {workspace.initials}
                        </span>
                      </Mtds.Button>
                    </li>
                  ))}
                  <li>
                    <Mtds.Button
                      as={Link}
                      data-tooltip='Vis alle'
                      to={`/workspaces?kind=${group.kind.code}`}
                    >
                      <CaretRightIcon />
                    </Mtds.Button>
                  </li>
                </menu>
              </React.Fragment>
            ))}

          <Mtds.Divider />
          <menu>
            {isAdmin && (
              <>
                <li>
                  <Mtds.Button
                    as={NavLink}
                    data-tooltip='Viktige meldinger'
                    to='/important-messages'
                  >
                    <ChatCenteredIcon />
                  </Mtds.Button>
                </li>
                <li>
                  <Mtds.Button
                    as={NavLink}
                    data-tooltip='Kontrakter'
                    to='/contracts'
                  >
                    <HandshakeIcon />
                  </Mtds.Button>
                </li>
              </>
            )}
            <li>
              <Mtds.Button
                as={NavLink}
                data-tooltip='Innstillinger'
                to='/settings'
              >
                <GearIcon />
              </Mtds.Button>
            </li>
          </menu>
        </Mtds.App.Sticky>
      </Mtds.App.Sidebar>
      <UserFeedback appName='proveplan' />
    </>
  )
}
